import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Don't probe tenant-user auth on the super-admin console, the public
    // signup flow, or the public clinic landing / booking pages — those
    // are anonymous-first surfaces with their own auth realm (or none).
    // Skipping the probe avoids a needless 401 on incognito visits that
    // used to cascade into the "public page keeps bouncing to login" bug.
    const p = window.location.pathname;
    if (
      p.startsWith('/superadmin') ||
      p.startsWith('/signup')     ||
      p.startsWith('/c/')         ||
      p.startsWith('/book/')      ||
      p === '/'
    ) {
      setLoading(false);
      return;
    }
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const logout = async () => {
    // Resolve where to land BEFORE we touch user state — that way the
    // user never flashes through the central /login between sign-out
    // and the branded tenant login.
    let slug = (new URLSearchParams(window.location.search).get('tenant') || '').toLowerCase();
    if (!slug) slug = (window.localStorage?.getItem('tenantSlug') || '').toLowerCase();
    if (!slug) {
      try {
        const { data } = await axios.get(`${API_URL}/api/platform/config`);
        const cfg = data || {};
        const host = window.location.hostname.toLowerCase();
        const baseDomain = (cfg.app_base_domain || '').toLowerCase();
        if (baseDomain && host.endsWith(`.${baseDomain}`) && host !== baseDomain) {
          slug = host.slice(0, host.length - baseDomain.length - 1);
        }
        if (!slug) slug = (cfg.default_tenant_slug || '').toLowerCase();
      } catch { /* fall through */ }
    }
    // Fire-and-forget the logout call so the browser doesn't wait on it.
    axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    // Hard replace — replaces the current entry so back-button doesn't
    // bounce the user into a half-authenticated app.
    window.location.replace(slug ? `/login?tenant=${slug}` : '/login');
  };

  if (loading) {
    // Don't block the entire app — individual ProtectedRoute / PublicRoute
    // wrappers handle their own loading state, and the super-admin /
    // landing pages don't need to wait for tenant-user auth.
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
